import React from 'react';
import { Button, Space, Tag, Tooltip, Modal } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { formatDate } from '../helper/helper';
import { StatusTag } from './StatusTag';

interface TableColumnHelpers {
  onEditDraft: (record: any) => void;
  onDeleteDraft: (record: any) => void;
  onRefreshDraft: () => void;
}

// Table columns for SMS draft
export const getSmsDraftColumns = ({
  onEditDraft,
  onDeleteDraft,
}: Omit<TableColumnHelpers, 'onRefreshDraft'>) => [
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    width: 88,
    render: (status: string) => <StatusTag status={status} />,
  },
  {
    title: 'Content',
    key: 'content',
    width: 140,
    ellipsis: true,
    render: (record: any) => {
      const content = record.content || record.message || '-';
      return (
        <Tooltip title={content}>
          <div
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '140px',
              fontSize: '12px',
              lineHeight: '1.4',
              color: '#333',
            }}
          >
            {content}
          </div>
        </Tooltip>
      );
    },
  },
  {
    title: 'Date & Time',
    dataIndex: 'created_at',
    key: 'created_at',
    width: 118,
    render: (date: string) => (
      <div style={{ lineHeight: '1.3' }}>
        <div
          style={{
            fontSize: '12px',
            fontWeight: '500',
            color: '#333',
          }}
        >
          {formatDate(date, 'MM/DD/YYYY')}
        </div>
        <div
          style={{
            fontSize: '11px',
            color: '#666',
          }}
        >
          {new Date(date).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
          })}
        </div>
      </div>
    ),
  },
  {
    title: 'Actions',
    key: 'actions',
    width: 110,
    render: (record: any) => (
      <Space size="small">
        <Button
          type="primary"
          icon={<EditOutlined />}
          size="small"
          onClick={() => onEditDraft(record)}
          style={{
            fontSize: '12px',
            height: '28px',
            padding: '0 8px',
          }}
        >
          Edit
        </Button>
        <Button
          type="primary"
          danger
          icon={<DeleteOutlined />}
          size="small"
          onClick={() => {
            Modal.confirm({
              title: 'Delete Draft',
              content: 'Do you really want to delete this draft?',
              okText: 'Yes',
              okType: 'danger',
              cancelText: 'No',
              onOk: () => onDeleteDraft(record),
            });
          }}
          style={{
            fontSize: '12px',
            height: '28px',
            padding: '0 8px',
          }}
        >
          Delete
        </Button>
      </Space>
    ),
  },
];

// Table columns for Email draft
export const getEmailDraftColumns = ({
  onEditDraft,
  onDeleteDraft,
}: Omit<TableColumnHelpers, 'onRefreshDraft'>) => [
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    width: 88,
    render: (status: string) => <StatusTag status={status} />,
  },
  {
    title: 'Title',
    key: 'title',
    width: 120,
    ellipsis: true,
    render: (record: any) => {
      const subject = record.subject || '-';
      return (
        <Tooltip title={subject}>
          <div
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '120px',
              fontSize: '12px',
              fontWeight: '500',
              lineHeight: '1.4',
              color: '#1890ff',
            }}
          >
            {subject}
          </div>
        </Tooltip>
      );
    },
  },
  {
    title: 'Content',
    key: 'content',
    width: 140,
    ellipsis: true,
    render: (record: any) => {
      const content = record.text || record.content || '';
      return (
        <Tooltip title={content ? <div dangerouslySetInnerHTML={{ __html: content }} style={{ maxWidth: 320 }} /> : '-'}>
          <div
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '140px',
              fontSize: '12px',
              lineHeight: '1.4',
              color: '#333',
            }}
          >
            {content ? (typeof content === 'string' ? content.replace(/<[^>]*>/g, '').slice(0, 80) || '-' : '-') : '-'}
          </div>
        </Tooltip>
      );
    },
  },
  {
    title: 'Date & Time',
    dataIndex: 'created_at',
    key: 'created_at',
    width: 118,
    render: (date: string) => (
      <div style={{ lineHeight: '1.3' }}>
        <div
          style={{
            fontSize: '12px',
            fontWeight: '500',
            color: '#333',
          }}
        >
          {formatDate(date, 'MM/DD/YYYY')}
        </div>
        <div
          style={{
            fontSize: '11px',
            color: '#666',
          }}
        >
          {new Date(date).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
          })}
        </div>
      </div>
    ),
  },
  {
    title: 'Actions',
    key: 'actions',
    width: 110,
    render: (record: any) => (
      <Space size="small">
        <Button
          type="primary"
          icon={<EditOutlined />}
          size="small"
          onClick={() => onEditDraft(record)}
          style={{
            fontSize: '12px',
            height: '28px',
            padding: '0 8px',
          }}
        >
          Edit
        </Button>
        <Button
          type="primary"
          danger
          icon={<DeleteOutlined />}
          size="small"
          onClick={() => {
            Modal.confirm({
              title: 'Delete Draft',
              content: 'Are you sure you want to delete this draft?',
              okText: 'Yes',
              okType: 'danger',
              cancelText: 'No',
              onOk: () => onDeleteDraft(record),
            });
          }}
          style={{
            fontSize: '12px',
            height: '28px',
            padding: '0 8px',
          }}
        >
          Delete
        </Button>
      </Space>
    ),
  },
];

const getRecipientName = (name: string) => {
  const m = name.match(/^\s*([^\(]+?)\s*\(\s*([^)]+)\s*\)\s*$/);
  return m;
};
// Table columns for SMS history
export const getSmsHistoryColumns = () => [
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    width: 88,
    render: (status: string) => <StatusTag status={status} />,
  },
  {
    title: 'Content',
    key: 'summary',
    width: 140,
    ellipsis: true,
    render: (record: any) => (
      <Tooltip title={record.content || '-'}>
        <div
          style={{
            lineHeight: '1.4',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '140px',
            fontSize: '12px',
            color: '#333',
          }}
        >
          {record.content || '-'}
        </div>
      </Tooltip>
    ),
  },
  {
    title: 'Recipients',
    key: 'recipients',
    width: 160,
    render: (record: any) => (
      <div style={{ lineHeight: '1.4' }}>
        {record.recipients?.length > 0 && (
          <div style={{ marginBottom: '8px' }}>
            <div
              style={{
                fontSize: '12px',
                fontWeight: '500',
                color: '#1890ff',
                marginBottom: '4px',
              }}
            >
              Recipients
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
              {record.recipients.map((item: any, index: number) => (
                <Tag
                  key={index}
                  color="blue"
                  style={{
                    fontSize: '11px',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    margin: '0',
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                    maxWidth: '150px',
                  }}
                >
                  {`<Contact: GradMap>`} {getRecipientName(item?.name) ? ` <${getRecipientName(item?.name)?.[1]}>` : '<N/A>'}
                  {getRecipientName(item?.name) ? ` <${getRecipientName(item?.name)?.[1]}>` : '<N/A>'}
                </Tag>
              ))}
            </div>
          </div>
        )}
      </div>
    ),
  },
  {
    title: 'Date & Time',
    dataIndex: 'created_at',
    key: 'created_at',
    width: 118,
    render: (date: string) => (
      <div style={{ lineHeight: '1.3' }}>
        <div
          style={{
            fontSize: '12px',
            fontWeight: '500',
            color: '#333',
          }}
        >
          {formatDate(date, 'MM/DD/YYYY')}
        </div>
        <div
          style={{
            fontSize: '11px',
            color: '#666',
          }}
        >
          {new Date(date).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
          })}
        </div>
      </div>
    ),
  },
  {
    title: 'Error',
    dataIndex: 'error_message',
    key: 'error_message',
    width: 72,
    render: (error: string) =>
      error ? (
        <Tooltip title={error}>
          <div
            style={{
              padding: '2px 6px',
              backgroundColor: '#fff2f0',
              border: '1px solid #ffccc7',
              borderRadius: '4px',
              fontSize: '10px',
              color: '#ff4d4f',
              cursor: 'pointer',
            }}
          >
            ⚠️
          </div>
        </Tooltip>
      ) : (
        <div
          style={{
            fontSize: '11px',
            color: '#52c41a',
            fontWeight: '500',
          }}
        >
          ✅
        </div>
      ),
  },
];

// Table columns for Email history
export const getEmailHistoryColumns = () => [
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    width: 88,
    render: (status: string) => <StatusTag status={status} />,
  },
  {
    title: 'Title',
    key: 'title',
    width: 120,
    ellipsis: true,
    render: (record: any) => {
      const subject = record.subject || '-';
      return (
        <Tooltip title={subject}>
          <div
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '120px',
              fontSize: '12px',
              fontWeight: '500',
              lineHeight: '1.4',
              color: '#1890ff',
            }}
          >
            {subject}
          </div>
        </Tooltip>
      );
    },
  },
  {
    title: 'Content',
    key: 'content',
    width: 140,
    ellipsis: true,
    render: (record: any) => {
      const content = record.text || record.content || '';
      return (
        <Tooltip title={content ? <div dangerouslySetInnerHTML={{ __html: content }} style={{ maxWidth: 320 }} /> : '-'}>
          <div
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '140px',
              fontSize: '12px',
              lineHeight: '1.4',
              color: '#333',
            }}
          >
            {content ? (typeof content === 'string' ? content.replace(/<[^>]*>/g, '').slice(0, 80) || '-' : '-') : '-'}
          </div>
        </Tooltip>
      );
    },
  },
  {
    title: 'Recipients',
    key: 'recipients',
    width: 160,
    render: (record: any) => (
      <div style={{ lineHeight: '1.4' }}>
        {record.recipients?.length > 0 && (
          <div style={{ marginBottom: '8px' }}>
            <div
              style={{
                fontSize: '12px',
                fontWeight: '500',
                color: '#1890ff',
                marginBottom: '4px',
              }}
            >
              Recipients
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
              {record.recipients.map((item: any, index: number) => (
                <Tag
                  key={index}
                  color="blue"
                  style={{
                    fontSize: '11px',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    margin: '0',
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                    maxWidth: '150px',
                  }}
                >
                  {`${item?.name || 'N/A'}`}
                </Tag>
              ))}
            </div>
          </div>
        )}
      </div>
    ),
  },
  {
    title: 'Date & Time',
    dataIndex: 'created_at',
    key: 'created_at',
    width: 118,
    render: (date: string) => (
      <div style={{ lineHeight: '1.3' }}>
        <div
          style={{
            fontSize: '12px',
            fontWeight: '500',
            color: '#333',
          }}
        >
          {formatDate(date, 'MM/DD/YYYY')}
        </div>
        <div
          style={{
            fontSize: '11px',
            color: '#666',
          }}
        >
          {new Date(date).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
          })}
        </div>
      </div>
    ),
  },
  {
    title: 'Error',
    dataIndex: 'error_message',
    key: 'error_message',
    width: 72,
    render: (error: string) =>
      error ? (
        <Tooltip title={error}>
          <div
            style={{
              padding: '2px 6px',
              backgroundColor: '#fff2f0',
              border: '1px solid #ffccc7',
              borderRadius: '4px',
              fontSize: '10px',
              color: '#ff4d4f',
              cursor: 'pointer',
            }}
          >
            ⚠️
          </div>
        </Tooltip>
      ) : (
        <div
          style={{
            fontSize: '11px',
            color: '#52c41a',
            fontWeight: '500',
          }}
        >
          ✅
        </div>
      ),
  },
];

